import { lazy, Suspense } from "react";
import HeroSection from "./components/HeroSection";
import MassachusettsCommunitiesWeServe from "./components/CommunitiesWeServe";
import SEOCrossLinks from "@/components/feature/SEOCrossLinks";
import CitySEOHead from "@/components/feature/CitySEOHead";
import { faqs as maFaqs } from "./components/FAQSection";

const CitiesDirectory = lazy(() => import("./components/CitiesDirectory"));
const StateResources = lazy(() => import("./components/StateResources"));
const FAQSection = lazy(() => import("./components/FAQSection"));
const FinalCTA = lazy(() => import("./components/FinalCTA"));

const massachusettsServices = [
  { name: "Emergency Passport Service", description: "Same-day and next-day emergency passport processing for Massachusetts travelers with urgent international travel needs." },
  { name: "Expedited Passport Renewal", description: "2–3 week expedited passport renewal service for Massachusetts residents." },
  { name: "New Passport Application", description: "First-time passport application assistance with document prep and acceptance facility scheduling for Massachusetts residents." },
  { name: "Child Passport Service", description: "Minor passport applications with parental consent documentation for Massachusetts families." },
  { name: "Lost or Stolen Passport Replacement", description: "Fast replacement of lost, stolen, or damaged passports for Massachusetts residents." },
  { name: "Name Change Passport Update", description: "Passport name change service after marriage, divorce, or legal name change for Massachusetts residents." },
];

export default function MassachusettsPage() {
  return (
    <>
      <CitySEOHead
        city="Massachusetts"
        state="Massachusetts"
        stateAbbr="MA"
        route="/states/massachusetts"
        title="Expedited & Emergency Passport Services Massachusetts | Same-Day MA Service | Fast Passport Center"
        description="Fast Passport Center provides emergency, expedited, and same-day passport services across all Massachusetts cities. Serving Boston, Worcester, Springfield, Lowell, Cambridge, and beyond. Same-day courier to the Boston National Passport Center."
        geoPlacename="Massachusetts"
        geoPosition="42.4072;-71.3824"
        faqs={maFaqs.map((f) => ({ q: f.question, a: f.answer }))}
        servicesSchema={massachusettsServices}
        keywords="expedited passport Massachusetts, emergency passport Massachusetts, same day passport MA, fast passport Boston, passport renewal Worcester, passport service Springfield"
      />
      <div className="min-h-screen bg-white">
        <HeroSection />
        <Suspense
          fallback={
            <div className="h-64 flex items-center justify-center bg-gray-50">
              <div className="animate-pulse text-gray-400 text-sm">Loading cities...</div>
            </div>
          }
        >
          <CitiesDirectory />
        </Suspense>
        <MassachusettsCommunitiesWeServe />
        <Suspense
          fallback={
            <div className="h-64 flex items-center justify-center bg-white">
              <div className="animate-pulse text-gray-400 text-sm">Loading resources...</div>
            </div>
          }
        >
          <StateResources />
        </Suspense>

        <section className="bg-[#F7F8FA] py-20 md:py-28">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div className="text-center mb-14">
              <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">
                Passport Services
              </p>
              <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">
                How We Serve Massachusetts
              </h2>
              <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">
                From the college corridors of Cambridge to the fishing ports of New Bedford, we help
                Massachusetts residents get their passports fast. The Boston National Passport Center
                processes applications — but appointments fill quickly. We cut through the red tape.
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                {
                  icon: "ri-flight-takeoff-line",
                  title: "Emergency Same-Day Service",
                  desc: "For Massachusetts travelers with flights within 72 hours. We hand-carry your application to the processing center.",
                },
                {
                  icon: "ri-time-line",
                  title: "Expedited 2–3 Week Service",
                  desc: "Standard expedited processing with overnight shipping and tracking for Massachusetts residents.",
                },
                {
                  icon: "ri-file-list-3-line",
                  title: "New Passport Applications",
                  desc: "First-time applicants in Massachusetts — we handle document prep, photos, and acceptance facility scheduling.",
                },
                {
                  icon: "ri-refresh-line",
                  title: "Passport Renewals",
                  desc: "Massachusetts renewals by mail or courier. We ensure your application is complete to avoid delays.",
                },
                {
                  icon: "ri-user-add-line",
                  title: "Child & Minor Passports",
                  desc: "Massachusetts families with kids under 16 — we guide you through parental consent and documentation.",
                },
                {
                  icon: "ri-error-warning-line",
                  title: "Lost, Stolen & Damaged",
                  desc: "Fast replacement for Massachusetts residents who need a new passport quickly.",
                },
              ].map((svc, i) => (
                <div
                  key={i}
                  className="bg-white rounded-lg p-7 border border-gray-100 hover:border-[#F6C344]/40 transition-all duration-200"
                >
                  <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#EEF2F8] mb-5">
                    <i className={`${svc.icon} text-[#1A365D] text-xl`}></i>
                  </div>
                  <h3 className="font-bold text-[#1A365D] text-base mb-2">{svc.title}</h3>
                  <p className="text-gray-500 text-sm leading-relaxed">{svc.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <Suspense
          fallback={
            <div className="h-64 flex items-center justify-center bg-gray-50">
              <div className="animate-pulse text-gray-400 text-sm">Loading FAQs...</div>
            </div>
          }
        >
          <FAQSection />
        </Suspense>

        <Suspense
          fallback={
            <div className="h-64 flex items-center justify-center bg-white">
              <div className="animate-pulse text-gray-400 text-sm">Loading CTA...</div>
            </div>
          }
        >
          <FinalCTA />
        </Suspense>

        <section className="bg-[#F7F8FA] py-16 md:py-20">
          <div className="max-w-4xl mx-auto px-4 md:px-8">
            <SEOCrossLinks state="MA" showCities={false} />
          </div>
        </section>
      </div>
    </>
  );
}